import axiosInstance from '@/app/api/axiosInstance';
import { 
  Task, 
  TaskCreate, 
  TaskUpdate, 
  TaskFilters, 
  TaskListResponse 
} from '@/app/types/task';
import { ListQueryParams } from '@/app/types/api';

const API_PATH = '/tasks';

export const taskService = {
  list: async (params?: TaskFilters & ListQueryParams): Promise<TaskListResponse> => {
    try {
      const response = await axiosInstance.get<TaskListResponse>(`${API_PATH}/`, { 
        params 
      });
      return response.data;
    } catch (error) {
      console.error('Failed to list tasks:', error);
      throw error;
    }
  },

  get: async (taskId: number, siteId?: number): Promise<Task> => {
    try {
      const params = siteId ? { site_id: siteId } : undefined;
      const response = await axiosInstance.get<Task>(`${API_PATH}/${taskId}`, { 
        params 
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to get task with ID ${taskId}:`, error);
      throw error;
    }
  },

  create: async (data: TaskCreate): Promise<Task> => {
    try {
      const response = await axiosInstance.post<Task>(`${API_PATH}/`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to create task:', error);
      throw error;
    }
  },

  update: async (taskId: number, data: TaskUpdate, siteId?: number): Promise<Task> => {
    try {
      const params = siteId ? { site_id: siteId } : undefined;
      const response = await axiosInstance.patch<Task>(`${API_PATH}/${taskId}`, data, { 
        params 
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to update task with ID ${taskId}:`, error);
      throw error;
    }
  },

  delete: async (taskId: number, siteId?: number): Promise<void> => {
    try {
      const params = siteId ? { site_id: siteId } : undefined;
      await axiosInstance.delete(`${API_PATH}/${taskId}`, { 
        params 
      });
    } catch (error) {
      console.error(`Failed to delete task with ID ${taskId}:`, error);
      throw error;
    }
  },

  getEntityTasks: async (
    entityType: string, 
    entityId: number, 
    includeRelated: boolean = true,
    siteId?: number
  ): Promise<Task[]> => {
    try {
      const params = {
        include_related: includeRelated,
        ...(siteId && { site_id: siteId })
      };
      const response = await axiosInstance.get<Task[]>(
        `${API_PATH}/entity/${entityType}/${entityId}`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get tasks for ${entityType}/${entityId}:`, error);
      throw error;
    }
  },

  getMyTasks: async (status?: string, limit: number = 100, skip: number = 0): Promise<TaskListResponse> => {
    try {
      const params = {
        ...(status && { status }),
        limit,
        skip
      };
      const response = await axiosInstance.get<TaskListResponse>(`${API_PATH}/my-tasks`, { 
        params 
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get my tasks:', error);
      throw error;
    }
  },

  markComplete: async (taskId: number, notes?: string): Promise<Task> => {
    try {
      const updateData: TaskUpdate = {
        status: 'completed' as any,
      };
      
      if (notes) {
        updateData.description = notes;
      }
      
      return await taskService.update(taskId, updateData);
    } catch (error) {
      console.error(`Failed to mark task ${taskId} as complete:`, error);
      throw error;
    }
  },

  markOpen: async (taskId: number): Promise<Task> => {
    try {
      const updateData: TaskUpdate = {
        status: 'open' as any,
      };
      
      return await taskService.update(taskId, updateData);
    } catch (error) {
      console.error(`Failed to mark task ${taskId} as open:`, error);
      throw error;
    }
  },

  markCancelled: async (taskId: number, reason?: string): Promise<Task> => {
    try {
      const updateData: TaskUpdate = {
        status: 'cancelled' as any,
      };

      if (reason) {
        updateData.description = reason;
      }

      return await taskService.update(taskId, updateData);
    } catch (error) {
      console.error(`Failed to mark task ${taskId} as cancelled:`, error);
      throw error;
    }
  },

  // Claim a team-pool task (assign it to the current user). 409 if already claimed.
  claim: async (taskId: number): Promise<Task> => {
    const response = await axiosInstance.post<Task>(`${API_PATH}/${taskId}/claim`);
    return response.data;
  },

  // Release a claimed task back to its team pool (clears the assignee).
  release: async (taskId: number): Promise<Task> => {
    const response = await axiosInstance.post<Task>(`${API_PATH}/${taskId}/release`);
    return response.data;
  }
};

export const createTask = taskService.create;
export const updateTask = taskService.update;
export const deleteTask = taskService.delete;
export const listTasks = taskService.list;
export const getTask = taskService.get;
export const getEntityTasks = taskService.getEntityTasks;
export const getMyTasks = taskService.getMyTasks;
export const markTaskComplete = taskService.markComplete;
export const markTaskOpen = taskService.markOpen;
export const markTaskCancelled = taskService.markCancelled;
export const claimTask = taskService.claim;
export const releaseTask = taskService.release;